export const SALE_LINE_REQUIRED_IDS = [
  "productGroup",
  "grWt",
  "netWt",
  "itRateOn",
  "itRate",
  "itAmt",
  "totalAmt",
];

export const PURCHASE_LINE_REQUIRED_IDS = [
  "productGroup",
  "grWt",
  "netWt",
  "purity",
  "fineWt",
];

export const SALE_LINE_ERROR_OPTIONS = {
  requiredIds: SALE_LINE_REQUIRED_IDS,
  requirePcsWithoutTag: true,
};

export const PURCHASE_LINE_ERROR_OPTIONS = {
  requiredIds: PURCHASE_LINE_REQUIRED_IDS,
  requirePcsWithoutTag: false,
};

export const PURCHASE_RATE_CUT_LINE_ERROR_OPTIONS = {
  requiredIds: [
    ...PURCHASE_LINE_REQUIRED_IDS,
    "itRateOn",
    "itRate",
    "itAmt",
    "totalAmt",
  ],
  requirePcsWithoutTag: true,
};

export const EDIT_PURCHASE_LINE_ERROR_OPTIONS = {
  requiredIds: [
    "metalGroup",
    "grWt",
    "netWt",
    "purity",
    "fineWt",
  ],
  requirePcsWithoutTag: false,
  productGroupId: "metalGroup",
};

export const isLineValueMissing = (value) =>
  value == null || String(value).trim() === "";

const cellValue = (row, fieldId) =>
  row?.data?.find((item) => item.id === fieldId)?.value;

export function rowHasLineEntry(row, defaultValueIds = ["itRateOn", "lType"]) {
  return (row?.data || []).some((cell) => {
    if (cell.id === "no") return false;
    if (cell.id === "image" && cell.imageFile) return true;
    if (defaultValueIds.includes(cell.id)) {
      const value = String(cell.value || "").trim();
      return value !== "" && value !== "NWt" && value !== "FWt";
    }
    return !isLineValueMissing(cell.value);
  });
}

export function collectLineItemFieldErrors(rows, options = {}) {
  const {
    requiredIds = SALE_LINE_REQUIRED_IDS,
    requirePcsWithoutTag = false,
    productGroupId = "productGroup",
    defaultValueIds = ["itRateOn", "lType"],
    zeroAsMissingIds = [],
  } = options;

  const list = Array.isArray(rows) ? rows : [];
  const started = list.filter((row) => rowHasLineEntry(row, defaultValueIds));
  const targets = started.length ? started : list.slice(0, 1);
  const errors = {};

  targets.forEach((row) => {
    const missing = requiredIds.filter((fieldId) => {
      const value = cellValue(row, fieldId);
      if (zeroAsMissingIds.includes(fieldId)) {
        if (isLineValueMissing(value)) return true;
        const num = parseFloat(value);
        return !Number.isFinite(num) || num <= 0;
      }
      return isLineValueMissing(value);
    });
    if (
      requirePcsWithoutTag &&
      !isLineValueMissing(cellValue(row, productGroupId)) &&
      isLineValueMissing(cellValue(row, "tagNo")) &&
      isLineValueMissing(cellValue(row, "pcs")) &&
      !missing.includes("pcs")
    ) {
      missing.push("pcs");
    }
    if (missing.length) errors[row.id] = missing;
  });

  return errors;
}

export function pruneLineFieldErrors(prev, fresh) {
  const rowIds = Object.keys(prev || {});
  if (!rowIds.length) return prev;

  let changed = false;
  const next = {};
  rowIds.forEach((rowId) => {
    const stillMissing = (prev[rowId] || []).filter((fieldId) =>
      fresh[rowId]?.includes(fieldId),
    );
    if (stillMissing.length !== (prev[rowId] || []).length) changed = true;
    if (stillMissing.length) next[rowId] = stillMissing;
  });

  return changed ? next : prev;
}

export function lineCellErrorStyle(hasError) {
  if (!hasError) return {};
  return {
    verticalAlign: "top",
    backgroundColor: "#fff5f5",
    boxShadow: "inset 0 0 0 2px #dc3545",
  };
}

export function fieldControlStyle(hasError) {
  if (!hasError) return {};
  return {
    borderColor: "#dc3545",
    backgroundColor: "#fff5f5",
    boxShadow: "inset 0 0 0 1px #dc3545",
  };
}

export function withSelectErrorStyles(styles, hasError) {
  if (!hasError) return styles;
  const baseControl = styles?.control;
  return {
    ...styles,
    control: (provided, state) => {
      const next =
        typeof baseControl === "function"
          ? baseControl(provided, state)
          : provided;
      return {
        ...next,
        borderColor: "#dc3545",
        borderWidth: 2,
        backgroundColor: "#fff5f5",
        boxShadow: "none",
        "&:hover": { borderColor: "#dc3545" },
      };
    },
  };
}

export const CUSTOM_ORDER_LINE_ERROR_OPTIONS = {
  requiredIds: ["productGroup", "grWt", "totalAmt"],
  zeroAsMissingIds: ["grWt"],
};

export const EDIT_CUSTOM_ORDER_LINE_ERROR_OPTIONS = {
  requiredIds: ["productGroup", "grWt", "netWt"],
  zeroAsMissingIds: ["grWt", "netWt"],
};

export const REPAIRING_ORDER_LINE_ERROR_OPTIONS = {
  requiredIds: ["productGroup", "currentWeight"],
  zeroAsMissingIds: ["currentWeight"],
};

export const JANGAD_LINE_ERROR_OPTIONS = {
  requiredIds: ["productGroup", "pcs", "grWt"],
  zeroAsMissingIds: ["pcs", "grWt"],
};

const isQtyMissing = (value) => {
  if (isLineValueMissing(value)) return true;
  const num = parseFloat(value);
  return !Number.isFinite(num) || num <= 0;
};

export function collectJangadEntryErrors(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const started = list.filter(
    (row) =>
      !isLineValueMissing(row?.product_group) ||
      !isLineValueMissing(row?.product_group_name) ||
      !isLineValueMissing(row?.tag_no) ||
      !isLineValueMissing(row?.product_tag_no) ||
      !isQtyMissing(row?.pcs) ||
      !isQtyMissing(row?.gwt) ||
      !isLineValueMissing(row?.remarks),
  );
  const targets = started.length ? started : list.slice(0, 1);
  const errors = {};

  targets.forEach((row, index) => {
    const missing = [];
    if (
      isLineValueMissing(row?.product_group) &&
      isLineValueMissing(row?.product_group_name)
    ) {
      missing.push("product_group");
    }
    if (isQtyMissing(row?.pcs)) missing.push("pcs");
    if (isQtyMissing(row?.gwt)) missing.push("gwt");
    if (missing.length) errors[row?.id ?? index] = missing;
  });

  return errors;
}

const isPositiveQty = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) && num > 0;
};

export function collectOldMetalFieldErrors(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const started = list.filter(
    (row) =>
      String(row?.product_group_id || "").trim() ||
      String(row?.product_group_name || "").trim() ||
      isPositiveQty(row?.pcs) ||
      isPositiveQty(row?.gwt) ||
      isPositiveQty(row?.ct) ||
      isPositiveQty(row?.lwt),
  );
  const targets = started.length ? started : list.slice(0, 1);
  const errors = {};

  targets.forEach((row) => {
    const missing = [];
    if (!String(row?.product_group_id || "").trim()) {
      missing.push("product_group");
    }
    if (!isPositiveQty(row?.pcs) && !isPositiveQty(row?.gwt)) {
      missing.push("pcs", "gwt");
    }
    if (missing.length) errors[row.id] = missing;
  });

  return errors;
}
